// pages/TeacherDashboardPage.js
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Card, Stack, Grid, CircularProgress, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import {
  IconUsers,
  IconSchool,
  IconBook,
  IconCalendar,
  IconChartLine,
  IconChevronLeft
} from '@tabler/icons-react';
import { fetchUserInfo } from '../slices/accountSlice';
import { fetchClasses } from '../slices/classesSlice';
import { fetchSubjects } from '../slices/subjectsSlice';
import axios from 'axios';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

const AnimatedCard = motion(Card);
const MotionPaper = motion(Paper);

const TeacherDashboardPage = () => {
  const dispatch = useDispatch();
  const { list: classes } = useSelector((state) => state.classes);
  const { list: subjects } = useSelector((state) => state.subjects);
  const [loading, setLoading] = React.useState(true);
  const [scheduleData, setScheduleData] = React.useState([]);
  const [stats, setStats] = React.useState({
    myClasses: 0,
    mySubjects: 0,
    myStudents: 0,
    todaySchedule: 0
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await dispatch(fetchUserInfo());
        await dispatch(fetchClasses());
        await dispatch(fetchSubjects());
        
        // Fetch teacher's schedule
        const token = localStorage.getItem('accessToken');
        const headers = { 'Authorization': `Bearer ${token}` };
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        
        if (userInfo.employee_id) {
          const scheduleRes = await axios.get(
            `${BASE_URL}/students/teachers/${userInfo.employee_id}/schedule/`,
            { headers }
          );
          setScheduleData(scheduleRes.data || []);
        }
      } catch (error) {
        console.error('Error loading teacher dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [dispatch]);

  // Calculate stats
  const calculatedStats = useMemo(() => {
    const myClasses = classes?.filter(c => !c.is_archived) || [];
    const mySubjects = subjects?.filter(s => !s.is_archived) || [];
    
    // Count students from teacher's classes
    const myStudents = myClasses.reduce((sum, cls) => {
      return sum + (cls.students?.filter(s => !s.is_archived)?.length || 0);
    }, 0);
    
    // Count today's schedule items
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
    const todaySchedule = scheduleData.filter(s => {
      const scheduleDay = s.day_of_week?.toLowerCase();
      return scheduleDay === today.toLowerCase();
    }).length;
    
    return {
      myClasses: myClasses.length,
      mySubjects: mySubjects.length,
      myStudents,
      todaySchedule
    };
  }, [classes, subjects, scheduleData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#F8F9FF' }}>
        <CircularProgress sx={{ color: '#4338CA' }} />
      </Box>
    );
  }

  const statCards = [
    {
      title: 'صفوفي',
      value: calculatedStats.myClasses,
      icon: <IconSchool size={28} />,
      color: '#4338CA',
      bg: '#EEF2FF'
    },
    {
      title: 'موادي الدراسية',
      value: calculatedStats.mySubjects,
      icon: <IconBook size={28} />,
      color: '#5B21F4',
      bg: '#EEF2FF'
    },
    {
      title: 'طلابي',
      value: calculatedStats.myStudents,
      icon: <IconUsers size={28} />,
      color: '#0284C7',
      bg: '#E0F2FE'
    },
    {
      title: 'حصص اليوم',
      value: calculatedStats.todaySchedule,
      icon: <IconCalendar size={28} />,
      color: '#D97706',
      bg: '#FEF3C7'
    }
  ];

  let showEducationalSchedule = false;
  try {
    showEducationalSchedule = !!JSON.parse(localStorage.getItem('uiPreferences') || '{}')?.school?.educational_schedule;
  } catch {
    // Silently fail if preferences not available
  }

  const quickLinks = [
    { title: 'الصفوف', icon: <IconSchool size={28} />, color: '#4338CA', bg: '#EEF2FF', href: '/classes' },
    { title: 'المواد الدراسية', icon: <IconBook size={28} />, color: '#5B21F4', bg: '#EEF2FF', href: '/subjects' },
    ...(showEducationalSchedule
      ? [{ title: 'الجدول التعليمي', icon: <IconCalendar size={28} />, color: '#0284C7', bg: '#E0F2FE', href: '/educational-schedule' }]
      : []),
    { title: 'الطلاب', icon: <IconUsers size={28} />, color: '#D97706', bg: '#FEF3C7', href: '/students' }
  ];

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
      dir='rtl'
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconChartLine size={30} color="#fff" />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                لوحة تحكم المعلم
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                نظرة عامة على صفوفك وموادك وطلابك وحصص اليوم
              </Typography>
            </Box>
          </Stack>
        </MotionPaper>

        {/* Statistics Cards */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {statCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <AnimatedCard
                elevation={0}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: index * 0.08, ease: 'easeOut' }}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(67,56,202,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2.5,
                    bgcolor: card.bg,
                    color: card.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      color: '#1E1B4B',
                      lineHeight: 1.2,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  >
                    {card.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                      fontWeight: 600,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  >
                    {card.title}
                  </Typography>
                </Box>
              </AnimatedCard>
            </Grid>
          ))}
        </Grid>

        {/* Quick Access Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.35, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: '#1E1B4B',
              mb: 2,
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            الوصول السريع
          </Typography>
          <Grid container spacing={2}>
            {quickLinks.map((link, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: '1px solid #E5E7EB',
                    bgcolor: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 24px rgba(67,56,202,0.1)',
                      borderColor: link.color,
                    },
                  }}
                  onClick={() => window.location.href = link.href}
                >
                  <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: link.bg,
                        color: link.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {link.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#1E1B4B',
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                      }}
                    >
                      {link.title}
                    </Typography>
                  </Stack>
                  <IconChevronLeft size={20} color="#6B7280" />
                </Card>
              </Grid>
            ))}
          </Grid>
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default TeacherDashboardPage;
